import React, { useState } from 'react';
import { usePaginatedSubscribers, useUnsubscribe } from '@/lib/api/hooks/subscriber';
import type { Subscriber } from '@/lib/api/types';
import { ActionDropdown, Button, Loader, Pagination, TanStackTable, type ColumnDef, type DropdownAction } from '@/components/ui';
import { SearchIcon, ExportIcon } from '@/components/icons';
import { showToast } from '@/lib/toast';
import SubscribersStats from './SubscribersStats';

const Subscribers: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(new Set());
  const [unsubscribingEmail, setUnsubscribingEmail] = useState<string | null>(null);

     const [currentPage, setCurrentPage] = useState(1);
      const pageSize = 10;

  const { data: subscribersData, isLoading, error, refetch: refetchSubscribers } = usePaginatedSubscribers(
    { page:currentPage, size: pageSize },
    {}
  );

  const unsubscribeMutation = useUnsubscribe();

  const subscribers = subscribersData?.data?.data || [];
  const totalPages = subscribersData?.data?.totalPage || 1;

  const filteredSubscribers = subscribers.filter((subscriber: Subscriber) => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return subscriber.email.toLowerCase().includes(searchLower);
  });

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        time: date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      };
    } catch {
      return {
        date: dateString,
        time: ''
      };
    }
  };

  const getSubscriberName = (email: string) => {
    const localPart = email.split('@')[0] || '';
    const words = localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

    if (words.length === 0) return 'Subscriber';
    return words.join(' ');
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return 'SU';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const getAvatarColor = (subscriberId: string) => {
    const colors = [
      'bg-[#E8F6EE] text-[#0F8A47]',
      'bg-[#EEF2FF] text-[#4338CA]',
      'bg-[#FEF3C7] text-[#B45309]',
      'bg-[#FCE7F3] text-[#BE185D]',
      'bg-[#F3E8FF] text-[#7E22CE]'
    ];
    const code = subscriberId.charCodeAt(0) || 0;
    return colors[code % colors.length];
  };

  const getDropdownActions = (subscriber: Subscriber): DropdownAction[] => [
    {
      label: 'Unsubscribe',
      onClick: (e) => {
        e.stopPropagation();
        handleUnsubscribe(subscriber.email);
      },
      variant: 'danger' as const,
      disabled: unsubscribingEmail === subscriber.email
    }
  ];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubscribers(new Set(filteredSubscribers.map((s: Subscriber) => s.subscriberId)));
    } else {
      setSelectedSubscribers(new Set());
    }
  };

  const handleSelectSubscriber = (subscriberId: string, checked: boolean) => {
    const newSelected = new Set(selectedSubscribers);
    if (checked) {
      newSelected.add(subscriberId);
    } else {
      newSelected.delete(subscriberId);
    }
    setSelectedSubscribers(newSelected);
  };

  const handleExport = () => {
    try {
      // Determine which subscribers to export
      const subscribersToExport = selectedSubscribers.size > 0
        ? filteredSubscribers.filter((s: Subscriber) => selectedSubscribers.has(s.subscriberId))
        : filteredSubscribers;

      if (subscribersToExport.length === 0) {
        showToast.error('No subscribers to export');
        return;
      }

      // Create CSV headers
      const headers = ['Email', 'Join Date', 'Status'];
      
      // Create CSV rows
      const rows = subscribersToExport.map((subscriber: Subscriber) => {
        const email = subscriber.email;
        const joinDate = formatDateTime(subscriber.dateJoined).date;
        const status = subscriber.isActive ? 'Active' : 'Inactive';
        
        // Escape commas and quotes in CSV values
        const escapeCSV = (value: string) => {
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        };

        return [
          escapeCSV(email),
          escapeCSV(joinDate),
          escapeCSV(status)
        ].join(',');
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows
      ].join('\n');

      // Add BOM for Excel compatibility with special characters
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `subscribers_${timestamp}.csv`;
      
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast.success(
        `Exported ${subscribersToExport.length} subscriber${subscribersToExport.length === 1 ? '' : 's'} successfully`
      );
    } catch (error) {
      console.error('Export error:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to export subscribers. Please try again.');
    }
  };

  const handleUnsubscribe = async (email: string) => {
    setUnsubscribingEmail(email);
    try {
      await unsubscribeMutation.mutateAsync(email);
      await refetchSubscribers();
    } catch (error) {
      console.error('Unsubscribe error:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to unsubscribe. Please try again.');
    } finally {
      setUnsubscribingEmail(null);
    }
  };

  const isAllSelected = filteredSubscribers.length > 0 && selectedSubscribers.size === filteredSubscribers.length;
  const isIndeterminate = selectedSubscribers.size > 0 && selectedSubscribers.size < filteredSubscribers.length;

  const columns: ColumnDef<Subscriber, unknown>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={isAllSelected}
          ref={(input) => {
            if (input) {
              input.indeterminate = isIndeterminate;
            }
          }}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="checkbox-wine"
          aria-label="Select all subscribers"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedSubscribers.has(row.original.subscriberId)}
          onChange={(e) => handleSelectSubscriber(row.original.subscriberId, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="checkbox-wine"
          aria-label={`Select ${row.original.email}`}
        />
      ),
      meta: {
        headerClassName: 'w-10 px-4 normal-case',
        cellClassName: 'w-10',
      },
    },
    {
      id: 'subscriber',
      header: 'Subscriber',
      cell: ({ row }) => {
        const subscriber = row.original;
        const name = getSubscriberName(subscriber.email);
        const initials = getInitials(name);
        const avatarColor = getAvatarColor(subscriber.subscriberId);

        return (
          <div className="flex items-center gap-3 min-w-[260px]">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold ${avatarColor}`}>
              {initials}
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900">{name}</p>
              <p className="text-sm text-gray-500">{subscriber.email}</p>
            </div>
          </div>
        );
      },
      meta: {
        headerClassName: 'normal-case px-4',
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const isActive = row.original.isActive;
        return (
          <span className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium ${
            isActive ? 'bg-[#E8F6EE] text-[#0F8A47]' : 'bg-[#FDE8EB] text-[#B42339]'
          }`}>
            <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-[#0F8A47]' : 'bg-[#B42339]'}`} />
            {isActive ? 'Active' : 'Unsubscribed'}
          </span>
        );
      },
      meta: {
        headerClassName: 'normal-case px-4',
      },
    },
    {
      id: 'source',
      header: 'Source',
      cell: ({ row }) => {
        const source = (row.original as Subscriber & { source?: string }).source || 'Website Signup';
        return <span className="text-sm text-gray-700">{source}</span>;
      },
      meta: {
        headerClassName: 'normal-case px-4',
      },
    },
    {
      id: 'dateJoined',
      header: 'Date Joined',
      cell: ({ row }) => {
        const value = formatDateTime(row.original.dateJoined);
        return (
          <div>
            <p className="text-sm  text-gray-900">{value.date}</p>
            {value.time && <p className="text-sm text-gray-500">{value.time}</p>}
          </div>
        );
      },
      meta: {
        headerClassName: 'normal-case px-4',
      },
    },
    {
      id: 'lastActivity',
      header: () => (
        <span className="inline-flex items-center gap-2 normal-case">
          Last Activity
        </span>
      ),
      cell: ({ row }) => {
        const value = formatDateTime(row.original.dateJoined);
        return (
          <div>
            <p className="text-sm  text-gray-900">{value.date}</p>
            {value.time && <p className="text-sm text-gray-500">{value.time}</p>}
          </div>
        );
      },
      meta: {
        headerClassName: 'normal-case px-4',
      },
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ActionDropdown
            id={`subscriber-${row.original.subscriberId}`}
            actions={getDropdownActions(row.original)}
          />
        </div>
      ),
      meta: {
        headerClassName: 'w-14',
        cellClassName: 'w-14',
      },
    },
  ];

  return (
    <div className="space-y-6">
      <SubscribersStats />

      {/* Search and Export */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:outline-none"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
        </div>
        <Button
          variant="secondary"
          icon={<ExportIcon />}
          onClick={handleExport}
          disabled={filteredSubscribers.length === 0}
        >
          Export {selectedSubscribers.size > 0 ? `(${selectedSubscribers.size})` : 'All'}
        </Button>
      </div>

      {/* Subscribers Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader size="md" variant="wine" message="Loading subscribers..." />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="inline-block bg-red-100 p-4 rounded-full">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="mt-4 font-semibold text-gray-800 text-lg">Error Loading Subscribers</h3>
            <p className="mt-1 text-sm text-gray-500">{error.message || 'Something went wrong'}</p>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-block bg-gray-200/20 p-4 rounded-full">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="mt-4 font-semibold text-gray-800 text-lg">No Subscribers</h3>
            <p className="mt-1 text-sm text-gray-500">No subscribers found</p>
          </div>
        ) : (
          <>
          <TanStackTable<Subscriber>
            data={filteredSubscribers}
            columns={columns}
            getRowId={(row) => row.subscriberId}
            className="w-full overflow-x-auto"
            tableClassName="w-full min-w-[1100px] divide-y divide-gray-200"
            theadClassName="bg-white border-b border-gray-200"
            tbodyClassName="bg-white divide-y divide-gray-200"
            emptyMessage="No subscribers found"
            rowClassName="hover:bg-gray-50 transition-colors"
          />

              {/* Pagination */}
                                  
                                          <Pagination
                                              currentPage={currentPage}
                                              totalPages={totalPages}
                                              onPageChange={setCurrentPage}
                                              className="mt-6 p-4"
                                          />
          </>
        )}
      </div>
    </div>
  );
};

export default Subscribers;
